import { PocDivider } from 'commons/components/components-wrapper/poc-divider.component';
import { PocSwitch } from 'commons/components/components-wrapper/poc-switch.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import type { PocInputValidity } from 'commons/types';
import { useTranslation } from 'core/i18n/i18n';
import { CleaningProperties } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.model';
import { DeviceConfigStatusTrackingCard } from '../device-config-status-tracking-card/device-config-status-tracking-card.component';
import {
    DeviceConfigStatusTrackingDescriptionCard,
    DeviceConfigStatusTrackingRow,
    DeviceConfigStatusTrackingRowTypography,
} from '../status-tracking-row.style';
import { StatusTrackingThresholdRow } from '../status-tracking-threshold-row/status-tracking-threshold-row.component';
import { STATUS_TRACKING_TRANSLATION_PREFIX } from '../../constants';
import { MIN_CLEANING_HOURS } from './cleaning-status-tracking-card.constants';
import { OptionalSettingsLayout, OptionalSettingTextLayout } from './cleaning-status-tracking-card.style';
import { useCleaningStatusTrackingCard } from './use-cleaning-status-tracking-card.hook';

interface CleaningStatusTrackingCardProps {
    values: CleaningProperties;
    onValuesChange: (values: CleaningProperties) => void;
    onValidityChange: (isValid: PocInputValidity) => void;
}

export const CleaningStatusTrackingCard = ({
    values,
    onValuesChange,
    onValidityChange,
}: CleaningStatusTrackingCardProps) => {
    const { t } = useTranslation();
    const { cleaningStatus, validity, validationMessage, handleChange } = useCleaningStatusTrackingCard({
        values,
        onValuesChange,
        onValidityChange,
    });

    return (
        <DeviceConfigStatusTrackingCard
            title={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.title`)}
            description={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.description`)}
            toggleChecked={cleaningStatus.enabled}
            onToggleCheckedChange={(value) => handleChange('enabled', value)}
        >
            <StatusTrackingThresholdRow
                tagStatus="warning"
                label={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.warning`)}
                value={cleaningStatus.warningHours}
                minHours={MIN_CLEANING_HOURS}
                disabled={!cleaningStatus.enabled}
                validity={validity}
                assistiveText={validationMessage}
                onChange={(value) => handleChange('warningHours', value)}
            />
            <PocDivider />
            <StatusTrackingThresholdRow
                tagStatus="alarm"
                label={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.alert`)}
                value={cleaningStatus.overdueHours}
                minHours={MIN_CLEANING_HOURS}
                disabled={!cleaningStatus.enabled}
                validity={validity}
                onChange={(value) => handleChange('overdueHours', value)}
            />
            {cleaningStatus.supportsOperatorComments && (
                <OptionalSettingsLayout>
                    <PocDivider />
                    <PocTypography variant="subtitle1">
                        {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.optionalSettings`)}
                    </PocTypography>
                    <DeviceConfigStatusTrackingRow>
                        <OptionalSettingTextLayout>
                            <DeviceConfigStatusTrackingRowTypography>
                                {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.showOperatorComments.title`)}
                            </DeviceConfigStatusTrackingRowTypography>
                            <DeviceConfigStatusTrackingDescriptionCard>
                                {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.showOperatorComments.description`)}
                            </DeviceConfigStatusTrackingDescriptionCard>
                        </OptionalSettingTextLayout>
                        <PocSwitch
                            aria-label={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.cleaning.showOperatorComments.title`)}
                            checked={cleaningStatus.showOperatorComments}
                            disabled={!cleaningStatus.enabled}
                            onValueChange={(e) => handleChange('showOperatorComments', e.detail)}
                        />
                    </DeviceConfigStatusTrackingRow>
                </OptionalSettingsLayout>
            )}
        </DeviceConfigStatusTrackingCard>
    );
};
